
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, OnDestroy, inject , signal } from "@angular/core";
import { TaskService } from "../task.service";
import { TaskFlowService } from "./task-flow.service";
import { select, Store } from "@ngrx/store";
import { LoadTasks, LoadTasksSuccess, getIsTasksNextPageLoading, getIsTasksIsMoreTasks, getTasksCurrentPageOffset, NextLoadTasks, NextLoadTasksSuccess, getSelectedFilterOption, getIsSearchMode, getSortBy, LoadTasksFail, NextLoadTasksFail, StoreProjectList, StorePriorityList, StoreMemberList, CreateTask, CreateTaskSuccess, CreateTaskFail, RemoveTasksSuccess, RemoveTasksFail, UpdateTask, UpdateTasksSuccess, UpdateTaskFail, UpdateTaskSuccess, StoreAuthUser, getAuthUser, RemoveTasks, AllowLoadMore, CreateTasksSuccess, SetTaskStatisticsInfo, SetIsLoading, getSelectedFilterType, StoreTagList, getTagList, SetSearchStatisticsInfo, SetTagStatisticsInfo, UpdateReminderTasks, getReminderTasks, StoreFolderList, getFolderList, SetListStatisticsInfo, StoreMyTeamUsers, StoreSaveSearches, StoreLocationList, SetLocationStatisticsInfo, getLocationList, getAssignmentTasks, UpdateAssignmentTasks, getMemberList, SetTaskDetailHighlight, getTasks, SetTaskFilterType, SetTaskFilterOption, StoreSettings, getTasksById, getTaskStatisticsInfo, SetSelectAll, ResetSelectedTaskId, SetDetailView, SetEditTaskView } from "../store";
import { Task } from "../models/task";
import { TaskAttachment } from "../models/task-attachment";
import { TaskComment } from "../models/task-comment";
import { TasksRootState } from "../store/reducers/index";
import { TaskUtils } from "../shared/task-utils";
import { ErrorType, SuccessType, DateFilterType, BulkUpdateIssueType } from "../shared/task-enum";
import { TasksConstants } from "../shared/task-constacts";
import { Observable , of } from "rxjs";
import { SuccessService } from "../../common/providers/success-service";
import { ErrorService } from "../../common/providers/error-service";
import { TranslateService } from "@ngx-translate/core";
import { BulkUpdateArgs, AuthUser, User, FileUpload, TaskStatistics, Project, ICompact, MyUser, PendingOperation } from "../models/index";
import { ConfigService } from "../../common/providers/config.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { MessageTranslatorService } from "../services/message-translator-service";
import { LocalStorageService } from "../services/local-storage.service";
import { Subject, BehaviorSubject } from "rxjs";
import { DatabaseService } from "../services/database.service";
import { FilesStorageService } from "../services/files-storage.service";
import { CableService } from "../services/cable.service";
import { CommonUtil, MediaType, mobileonly } from "../../common/utils/common.utils";
import { AppConstants } from "../../common/utils/app-constants";
import { environment } from "../../../environments/environment";
import { IsDatabaseReady, getIsDeviceReady, getFederatedApps, getUserProfile, getAllContacts } from "../../reducers";
import { TaskNotificationType } from "../components/task-notifications";
import * as _ from "lodash";
import { getOnlineStatus, getLoginStatus } from "../../reducers";
import { Logout } from "../../actions/app";
import { List } from "../models/list";
import { VncLibraryService } from "@uxf/vnc-library";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "../models/location";
import { taskAdapter } from "../store/reducers/tasks.reducers";
import { takeUntil, distinctUntilChanged, debounceTime, take, filter, tap, delay, catchError, switchMap, finalize, takeWhile, map } from "rxjs/operators";
import { TaskWatchersComponent } from "../components";
import { DomSanitizer } from "@angular/platform-browser";
import { ElectronService } from "../services/electron.service";
import { ContactInfo } from "src/app/shared/models/contact-info.model";
import { AddVNCContacts, BulkLoadVNCContacts } from "src/app/actions/contact.action";
import { StorageService } from "../../common/services/storage.service";


// Mechanical decomposition layer of TaskRepository (Q-02). Behavior-preserving:
// fields/methods are unchanged, split across inheritance layers; the DI token
// remains the final TaskRepository class.
export abstract class TaskRepositoryStateBase {
  protected flowService = inject(TaskFlowService);

  protected taskService = inject(TaskService);

  protected store = inject<Store<TasksRootState>>(Store);

  protected errorService = inject(ErrorService);

  protected successService = inject(SuccessService);

  protected translate = inject(TranslateService);

  protected configService = inject(ConfigService);

  protected broadcaster = inject(Broadcaster);

  protected localStorageService = inject(LocalStorageService);

  protected databaseService = inject(DatabaseService);

  protected messageTranslatorService = inject(MessageTranslatorService);

  protected vncLibaryService = inject(VncLibraryService);

  protected filesStorageService = inject(FilesStorageService);

  protected cableService = inject(CableService);

  protected sanitizer = inject(DomSanitizer);

  protected electronService = inject(ElectronService);

  protected activated = inject(ActivatedRoute);

  protected router = inject(Router);

  /** Both live in TaskFlowService now (STR-07); accessors keep the replay and the
   * bulk-edit dialog working unchanged. */
  get FormattedDateForDB(): Date { return this.flowService.FormattedDateForDB; }
  set FormattedDateForDB(v: Date) { this.flowService.FormattedDateForDB = v; }

  protected msgs: any;

  AllTagListAndLocationTask = [];

  protected isAlive$ = new Subject<boolean>();

  protected pollingRemindersTimeInterval = 1; // min
  protected isPollingRemindersStarted: boolean;

  protected isPollingUpdateStarted: boolean;

  protected isPollingDeleteStarted: boolean;

  protected pollingUpdateTimeInterval = 1; // min
  protected pollingDeleteTimeInterval = 1; // min
  protected isLoggedIn: boolean = false;

  shouldPullOn: boolean = false;

  isOnline: any;

  isCordovaOrElectron = environment.isCordova || environment.isElectron;

  MediaType = MediaType;

  memberList: User[] = [];

  // Signals are the canonical state (P5); the $ streams are kept as compat
  // streams for existing subscribers and updated via the set* helpers below.
  readonly allUserList = signal<MyUser[]>(null);
  allUserList$: BehaviorSubject<MyUser[]> = new BehaviorSubject(null);

  readonly allTagList = signal<ICompact[]>(null);
  allTagList$: BehaviorSubject<ICompact[]> = new BehaviorSubject(null);

  protected setAllUserList(users: MyUser[]) {
    this.allUserList.set(users);
    this.allUserList$.next(users);
  }

  protected setAllTagList(tags: ICompact[]) {
    this.allTagList.set(tags);
    this.allTagList$.next(tags);
  }

  authUser: AuthUser;

  static taskStoreList = [
    "AllTaskStore",
    "NewTaskStore",
    "OpenTaskStore",
    "CompletedTaskStore",
    "AssignedTaskStore",
    "WeekTaskStore",
    "TodayTaskStore",
    "TomorrowTaskStore",
    "CreatedByTaskStore",
    "WatchTaskStore",
    "OverdueTaskStore",
  ];

  static statWithStore = [
    { stat: "all", store: "AllTaskStore" },
    { stat: "new", store: "NewTaskStore" },
    { stat: "open", store: "OpenTaskStore" },
    { stat: "completed", store: "CompletedTaskStore" },
    { stat: "assigned", store: "AssignedTaskStore" },
    { stat: "week", store: "WeekTaskStore" },
    { stat: "today", store: "TodayTaskStore" },
    { stat: "tomorrow", store: "TomorrowTaskStore" },
    { stat: "created", store: "CreatedByTaskStore" },
    { stat: "watch", store: "WatchTaskStore" },
    { stat: "overdue", store: "OverdueTaskStore" },
  ];

}

export interface SearchItem {
  id: string;
  title: string;
  objectId: string;
  contentTxt: string;
  chatType?: string;
  rawTxt?: string;
  createdDt: string;
  modifiedDt?: string;
  from: string;
  to: string[];
  type: string;
  version: number;
  shortContent?: string;
  parsedContent?: string;
  talkId?: string;
  owner?: string;
  mailFolderID?: string;
  unread?: boolean;
  flags?: string;
  metadata?: any;
  talk_attachment_s?:any;
}
